import { useState, useEffect, useCallback } from "react";
import { Loader2, ShoppingBag, Eye, Send, RefreshCw, Truck, Plus, Pencil, FileStack } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { formatGs, formatNumber, formatDateTime, ORDER_STATUS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const STATUS_OPTS = Object.keys(ORDER_STATUS);

const DELIVERY_STATUS = {
  no_enviado: { label: "No enviado", color: "bg-slate-100 text-slate-600" },
  enviado: { label: "Enviado", color: "bg-blue-100 text-blue-700" },
  en_proceso: { label: "En proceso", color: "bg-amber-100 text-amber-700" },
  completado: { label: "Completado", color: "bg-emerald-100 text-emerald-700" },
  parcial: { label: "Parcial", color: "bg-orange-100 text-orange-700" },
  cancelado: { label: "Cancelado", color: "bg-rose-100 text-rose-700" },
  error: { label: "Error", color: "bg-rose-100 text-rose-700" },
};

const Orders = () => {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", service_id: "", date_from: "", date_to: "" });
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/services").then((r) => setServices(r.data)); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Estado actualizado");
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [busy, setBusy] = useState(false);
  const deliver = async (o) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/deliver`);
      toast.success(`Enviado al proveedor (orden ${data.provider_order_id})`);
      const upd = { ...o, provider_order_id: data.provider_order_id, delivery_status: "enviado" };
      setSelected(upd); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sync = async (o) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.id}/sync`);
      toast.success(`Estado: ${data.delivery_status}`);
      setSelected({ ...o, delivery_status: data.delivery_status, start_count: data.start_count, remains: data.remains });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const emptyManual = { service_id: "", package_id: "", quantity: 1000, custom_price: "", instagram_username: "", customer_name: "", whatsapp: "", email: "", status: "pago_confirmado", payment_method: "whatsapp", note: "", useCustom: false };
  const [manualOpen, setManualOpen] = useState(false);
  const [mf, setMf] = useState(emptyManual);
  const selService = services.find((s) => s.id === mf.service_id);

  const createManual = async () => {
    if (!mf.service_id) return toast.error("Elegí un servicio");
    if (!mf.instagram_username.trim()) return toast.error("Ingresá el usuario de Instagram");
    const selPkg = selService?.packages?.find((p) => p.id === mf.package_id);
    const payload = {
      service_id: mf.service_id,
      package_id: mf.useCustom ? null : (mf.package_id || null),
      quantity: mf.useCustom ? Number(mf.quantity) : (selPkg?.quantity || Number(mf.quantity)),
      custom_price: mf.useCustom && mf.custom_price !== "" ? Number(mf.custom_price) : null,
      instagram_username: mf.instagram_username, customer_name: mf.customer_name,
      whatsapp: mf.whatsapp, email: mf.email, status: mf.status, payment_method: mf.payment_method, note: mf.note,
    };
    try {
      await api.post("/admin/orders", payload);
      toast.success("Pedido manual creado");
      setManualOpen(false); setMf(emptyManual); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- External order (registro de pedidos hechos fuera del sistema) ----
  const emptyExt = { provider_order_id: "", date: "", instagram_username: "", service_name: "", quantity: "", start_count: "", remains: "", cost: "", price: "", status: "completado", note: "" };
  const [extOpen, setExtOpen] = useState(false);
  const [ef, setEf] = useState(emptyExt);
  const createExternal = async () => {
    if (!ef.instagram_username.trim()) return toast.error("Ingresá el usuario o link de Instagram");
    if (!ef.service_name.trim()) return toast.error("Ingresá el nombre del servicio");
    const payload = {
      provider_order_id: ef.provider_order_id,
      date: ef.date ? new Date(ef.date).toISOString() : null,
      instagram_username: ef.instagram_username,
      service_name: ef.service_name,
      quantity: Number(ef.quantity) || 0,
      start_count: ef.start_count !== "" ? Number(ef.start_count) : null,
      remains: ef.remains !== "" ? Number(ef.remains) : null,
      cost: Number(ef.cost) || 0,
      price: Number(ef.price) || 0,
      status: ef.status,
      note: ef.note,
    };
    try {
      await api.post("/admin/orders/external", payload);
      toast.success("Pedido externo registrado");
      setExtOpen(false); setEf(emptyExt); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // ---- Edit existing order (owner only) ----
  const [editOpen, setEditOpen] = useState(false);
  const [edf, setEdf] = useState(null);
  const openEdit = (o) => {
    setEdf({ id: o.id, order_number: o.order_number, customer_name: o.customer_name || "", whatsapp: o.whatsapp || "", email: o.email || "", instagram_username: o.instagram_username || "", service_name: o.service_name || "", quantity: o.quantity ?? "", cost: o.cost ?? "", total: o.total ?? "", status: o.status, note: o.note || "" });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    const payload = {
      customer_name: edf.customer_name, whatsapp: edf.whatsapp, email: edf.email,
      instagram_username: edf.instagram_username, service_name: edf.service_name,
      quantity: Number(edf.quantity) || 0, cost: Number(edf.cost) || 0, total: Number(edf.total) || 0,
      status: edf.status, note: edf.note,
    };
    try {
      const { data } = await api.patch(`/admin/orders/${edf.id}`, payload);
      toast.success("Pedido actualizado");
      setEditOpen(false);
      if (selected?.id === edf.id) setSelected(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Pedidos" subtitle={`${orders.length} pedido(s)`}
        action={
          <div className="flex gap-2">
            {isOwner && (
              <Button onClick={() => { setEf(emptyExt); setExtOpen(true); }} variant="outline" className="rounded-lg" data-testid="new-external-order-btn"><FileStack className="h-4 w-4 mr-1" /> Pedido externo</Button>
            )}
            <Button onClick={() => { setMf(emptyManual); setManualOpen(true); }} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="new-manual-order-btn"><Plus className="h-4 w-4 mr-1" /> Pedido manual</Button>
          </div>
        } />

      {/* filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 grid sm:grid-cols-4 gap-3">
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-status" className="rounded-lg"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{ORDER_STATUS[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.service_id || "all"} onValueChange={(v) => setFilters({ ...filters, service_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-service" className="rounded-lg"><SelectValue placeholder="Servicio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los servicios</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="rounded-lg border border-slate-200 px-3 text-sm h-10" data-testid="filter-from" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="rounded-lg border border-slate-200 px-3 text-sm h-10" data-testid="filter-to" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
      ) : orders.length === 0 ? (
        <EmptyState icon={ShoppingBag} text="No hay pedidos con estos filtros." />
      ) : (
        <>
          {/* desktop table */}
          <div className="hidden lg:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Servicio</th>
                  <th className="text-left px-4 py-3">Usuario IG</th>
                  <th className="text-right px-4 py-3">Cant.</th>
                  <th className="text-right px-4 py-3">Valor</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Entrega</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50" data-testid={`order-row-${o.order_number}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">#{o.order_number}{o.source === "manual" && <span className="ml-1.5 text-[10px] font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full align-middle">Manual</span>}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDateTime(o.created_at)}</td>
                    <td className="px-4 py-3 text-slate-700">{o.customer_name || "—"}<br /><span className="text-xs text-slate-400">{o.whatsapp}</span></td>
                    <td className="px-4 py-3 text-slate-700">{o.service_name}</td>
                    <td className="px-4 py-3 text-slate-700">@{o.instagram_username}</td>
                    <td className="px-4 py-3 text-right">{formatNumber(o.quantity)}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatGs(o.total)}</td>
                    <td className="px-4 py-3">
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className={`h-8 text-xs rounded-full border-0 w-40 ${ORDER_STATUS[o.status]?.color}`} data-testid={`order-status-${o.order_number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{ORDER_STATUS[s].label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${DELIVERY_STATUS[o.delivery_status || "no_enviado"]?.color}`} data-testid={`order-delivery-${o.order_number}`}>{DELIVERY_STATUS[o.delivery_status || "no_enviado"]?.label}</span>
                      {(o.remains != null && o.delivery_status && o.delivery_status !== "no_enviado") && <span className="block text-[10px] text-slate-400 mt-0.5">Restante: {formatNumber(o.remains)}</span>}
                    </td>
                    <td className="px-4 py-3"><button onClick={() => setSelected(o)} data-testid={`order-view-${o.order_number}`}><Eye className="h-4 w-4 text-slate-400 hover:text-indigo-600" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="lg:hidden space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`order-card-${o.order_number}`}>
                <div className="flex justify-between items-start">
                  <div><p className="font-semibold text-slate-900">#{o.order_number} {o.source === "manual" && <span className="text-[10px] font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">Manual</span>}</p><p className="text-xs text-slate-400">{formatDateTime(o.created_at)}</p></div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ORDER_STATUS[o.status]?.color}`}>{ORDER_STATUS[o.status]?.label}</span>
                </div>
                <div className="mt-3 text-sm space-y-1">
                  <p className="text-slate-700">{o.service_name} · {formatNumber(o.quantity)}</p>
                  <p className="text-slate-500 text-xs">@{o.instagram_username} · {o.customer_name}</p>
                  <p className="font-heading font-bold text-slate-900">{formatGs(o.total)}</p>
                  <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full ${DELIVERY_STATUS[o.delivery_status || "no_enviado"]?.color}`}>Entrega: {DELIVERY_STATUS[o.delivery_status || "no_enviado"]?.label}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger className="h-9 text-xs rounded-lg flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{ORDER_STATUS[s].label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setSelected(o)}><Eye className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between pr-6">
                  <DialogTitle>Pedido #{selected.order_number}{selected.source === "externo" && <span className="ml-2 text-[10px] font-semibold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full align-middle">Externo</span>}</DialogTitle>
                  {isOwner && <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => openEdit(selected)} data-testid="edit-order-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Editar</Button>}
                </div>
              </DialogHeader>
              <div className="space-y-2.5 text-sm">
                {[
                  ["Fecha", formatDateTime(selected.created_at)],
                  ["Cliente", selected.customer_name || "—"],
                  ["WhatsApp", selected.whatsapp || "—"],
                  ["Email", selected.email || "—"],
                  ["Servicio", selected.service_name],
                  ["Usuario Instagram", "@" + selected.instagram_username],
                  ["Cantidad", formatNumber(selected.quantity)],
                  ["Subtotal", formatGs(selected.subtotal)],
                  ["Descuento", formatGs(selected.discount)],
                  ["Cupón", selected.coupon_code || "—"],
                  ["Total", formatGs(selected.total)],
                  ["Costo interno", formatGs(selected.cost)],
                  ["Método pago", selected.payment_method],
                  ["Suscripción", selected.subscription ? "Sí" : "No"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>
                ))}
                {selected.ai_analysis && (
                  <div className="rounded-lg bg-slate-50 p-3 text-xs">
                    <p className="font-semibold text-slate-700 mb-1">Análisis IA del comprobante</p>
                    <pre className="whitespace-pre-wrap text-slate-600">{JSON.stringify(selected.ai_analysis, null, 2)}</pre>
                  </div>
                )}

                <div className="rounded-lg border border-slate-200 p-3 mt-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5"><Truck className="h-4 w-4" /> Entrega automática</p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${DELIVERY_STATUS[selected.delivery_status || "no_enviado"]?.color}`}>{DELIVERY_STATUS[selected.delivery_status || "no_enviado"]?.label}</span>
                  </div>
                  {selected.provider_order_id && <p className="text-xs text-slate-500">Orden proveedor: <span className="font-medium">{selected.provider_order_id}</span></p>}
                  {(selected.start_count != null || selected.remains != null) && <p className="text-xs text-slate-500">Inicio: {selected.start_count ?? "—"} · Restante: {selected.remains ?? "—"}</p>}
                  {selected.delivery_error && <p className="text-xs text-rose-600 mt-1">{selected.delivery_error}</p>}
                  <div className="flex gap-2 mt-2">
                    {!selected.provider_order_id ? (
                      <Button size="sm" disabled={busy} onClick={() => deliver(selected)} className="rounded-lg bg-indigo-600 hover:bg-indigo-700 flex-1" data-testid="deliver-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Enviar al proveedor</>}
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => sync(selected)} className="rounded-lg flex-1" data-testid="sync-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Actualizar estado</>}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual order dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Crear pedido manual</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Servicio</Label>
              <Select value={mf.service_id} onValueChange={(v) => setMf({ ...mf, service_id: v, package_id: "" })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="manual-service"><SelectValue placeholder="Elegí un servicio" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Switch checked={mf.useCustom} onCheckedChange={(v) => setMf({ ...mf, useCustom: v })} data-testid="manual-custom-toggle" />
              Cantidad y precio personalizados
            </label>

            {!mf.useCustom ? (
              <div>
                <Label>Paquete</Label>
                <Select value={mf.package_id} onValueChange={(v) => setMf({ ...mf, package_id: v })} disabled={!selService}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="manual-package"><SelectValue placeholder="Elegí un paquete" /></SelectTrigger>
                  <SelectContent>
                    {(selService?.packages || []).sort((a, b) => a.quantity - b.quantity).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{formatNumber(p.quantity)} · {formatGs(p.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cantidad</Label><Input type="number" value={mf.quantity} onChange={(e) => setMf({ ...mf, quantity: e.target.value })} className="rounded-lg mt-1" data-testid="manual-quantity" /></div>
                <div><Label>Precio total (₲)</Label><Input type="number" value={mf.custom_price} onChange={(e) => setMf({ ...mf, custom_price: e.target.value })} className="rounded-lg mt-1" data-testid="manual-price" /></div>
              </div>
            )}

            <div><Label>Usuario de Instagram o link del perfil</Label><Input value={mf.instagram_username} onChange={(e) => setMf({ ...mf, instagram_username: e.target.value })} className="rounded-lg mt-1" placeholder="@usuario o https://instagram.com/usuario" data-testid="manual-username" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nombre del cliente <span className="text-slate-400 text-xs">(opcional)</span></Label><Input value={mf.customer_name} onChange={(e) => setMf({ ...mf, customer_name: e.target.value })} className="rounded-lg mt-1" data-testid="manual-name" /></div>
              <div><Label>WhatsApp <span className="text-slate-400 text-xs">(opcional)</span></Label><Input value={mf.whatsapp} onChange={(e) => setMf({ ...mf, whatsapp: e.target.value })} className="rounded-lg mt-1" data-testid="manual-whatsapp" /></div>
            </div>
            <div><Label>Email (opcional)</Label><Input value={mf.email} onChange={(e) => setMf({ ...mf, email: e.target.value })} className="rounded-lg mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Estado</Label>
                <Select value={mf.status} onValueChange={(v) => setMf({ ...mf, status: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="manual-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map((st) => <SelectItem key={st} value={st}>{ORDER_STATUS[st].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Método de pago</Label>
                <Select value={mf.payment_method} onValueChange={(v) => setMf({ ...mf, payment_method: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="manual-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="alias">Alias / Billetera</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Nota (opcional)</Label><Input value={mf.note} onChange={(e) => setMf({ ...mf, note: e.target.value })} className="rounded-lg mt-1" placeholder="Ej: pedido recibido por WhatsApp" data-testid="manual-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={createManual} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-manual-order">Crear pedido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* External order dialog (owner) */}
      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Registrar pedido externo</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Guardá en el sistema un pedido hecho por fuera (directo en el proveedor). No se envía nada al proveedor, es solo un registro.</p>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>ID del proveedor</Label><Input value={ef.provider_order_id} onChange={(e) => setEf({ ...ef, provider_order_id: e.target.value })} className="rounded-lg mt-1" placeholder="Ej: 846424" data-testid="ext-provider-id" /></div>
              <div><Label>Fecha</Label><Input type="datetime-local" value={ef.date} onChange={(e) => setEf({ ...ef, date: e.target.value })} className="rounded-lg mt-1" data-testid="ext-date" /></div>
            </div>
            <div><Label>Usuario o link de Instagram</Label><Input value={ef.instagram_username} onChange={(e) => setEf({ ...ef, instagram_username: e.target.value })} className="rounded-lg mt-1" placeholder="@usuario o https://instagram.com/usuario" data-testid="ext-username" /></div>
            <div><Label>Servicio (texto libre)</Label><Input value={ef.service_name} onChange={(e) => setEf({ ...ef, service_name: e.target.value })} className="rounded-lg mt-1" placeholder="Ej: INSTA-SEGUIDORES MISTOS / R30" data-testid="ext-service" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Cantidad</Label><Input type="number" value={ef.quantity} onChange={(e) => setEf({ ...ef, quantity: e.target.value })} className="rounded-lg mt-1" data-testid="ext-quantity" /></div>
              <div><Label>Contagem inicial</Label><Input type="number" value={ef.start_count} onChange={(e) => setEf({ ...ef, start_count: e.target.value })} className="rounded-lg mt-1" placeholder="Opcional" data-testid="ext-start" /></div>
              <div><Label>Restante</Label><Input type="number" value={ef.remains} onChange={(e) => setEf({ ...ef, remains: e.target.value })} className="rounded-lg mt-1" placeholder="Opcional" data-testid="ext-remains" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Costo proveedor (₲)</Label><Input type="number" value={ef.cost} onChange={(e) => setEf({ ...ef, cost: e.target.value })} className="rounded-lg mt-1" placeholder="Lo que pagaste" data-testid="ext-cost" /></div>
              <div><Label>Precio venta (₲) <span className="text-slate-400 text-xs">(opcional)</span></Label><Input type="number" value={ef.price} onChange={(e) => setEf({ ...ef, price: e.target.value })} className="rounded-lg mt-1" placeholder="0 si no aplica" data-testid="ext-price" /></div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="ext-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTS.map((st) => <SelectItem key={st} value={st}>{ORDER_STATUS[st].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nota (opcional)</Label><Input value={ef.note} onChange={(e) => setEf({ ...ef, note: e.target.value })} className="rounded-lg mt-1" placeholder="Ej: hecho por el socio vía WhatsApp" data-testid="ext-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={createExternal} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-external-order">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit order dialog (owner) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar pedido {edf && `#${edf.order_number}`}</DialogTitle></DialogHeader>
          {edf && (
            <div className="space-y-4">
              <div><Label>Servicio</Label><Input value={edf.service_name} onChange={(e) => setEdf({ ...edf, service_name: e.target.value })} className="rounded-lg mt-1" data-testid="edit-service" /></div>
              <div><Label>Usuario o link de Instagram</Label><Input value={edf.instagram_username} onChange={(e) => setEdf({ ...edf, instagram_username: e.target.value })} className="rounded-lg mt-1" data-testid="edit-username" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre del cliente</Label><Input value={edf.customer_name} onChange={(e) => setEdf({ ...edf, customer_name: e.target.value })} className="rounded-lg mt-1" data-testid="edit-name" /></div>
                <div><Label>WhatsApp</Label><Input value={edf.whatsapp} onChange={(e) => setEdf({ ...edf, whatsapp: e.target.value })} className="rounded-lg mt-1" data-testid="edit-whatsapp" /></div>
              </div>
              <div><Label>Email</Label><Input value={edf.email} onChange={(e) => setEdf({ ...edf, email: e.target.value })} className="rounded-lg mt-1" data-testid="edit-email" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Cantidad</Label><Input type="number" value={edf.quantity} onChange={(e) => setEdf({ ...edf, quantity: e.target.value })} className="rounded-lg mt-1" data-testid="edit-quantity" /></div>
                <div><Label>Costo (₲)</Label><Input type="number" value={edf.cost} onChange={(e) => setEdf({ ...edf, cost: e.target.value })} className="rounded-lg mt-1" data-testid="edit-cost" /></div>
                <div><Label>Total (₲)</Label><Input type="number" value={edf.total} onChange={(e) => setEdf({ ...edf, total: e.target.value })} className="rounded-lg mt-1" data-testid="edit-total" /></div>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={edf.status} onValueChange={(v) => setEdf({ ...edf, status: v })}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map((st) => <SelectItem key={st} value={st}>{ORDER_STATUS[st].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Nota</Label><Input value={edf.note} onChange={(e) => setEdf({ ...edf, note: e.target.value })} className="rounded-lg mt-1" data-testid="edit-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-lg">Cancelar</Button>
            <Button onClick={saveEdit} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-edit-order">Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
